import { Application, Container, Rectangle, Loader } from 'pixi.js-legacy';
import { containerEvents } from "./ContainerEvents";
import { selection } from "./editions/selection/Selection";
import { SelectionEditionMode } from "./editions/selection/SelectionEditionMode";
import { Shape } from "./helpers/Shape";
import { EntityManager, TYPES } from "./EntityManager";
import { RowService } from "./entity/row/RowService";
import { TextService } from "./entity/text/TextService";
import { ImageService } from './entity/image/ImageService';
import { PitService } from "./entity/pit/PitService";
import {
    graphicEvents,
    ON_CHANGE_TRANSLATE,
    ON_CHANGE_ZOOM,
    REMOVE_ENTITIES,
    SET_DUPLICATED_SEATS_COUNT, SET_TEXTS_DESCRIPTION, UPDATE_DESCRIPTION_TEXT
} from "./GraphicEvents";
import * as e from "./GraphicEvents";
import { stateManager } from "./StateManager";

import rotateIcon from '@assets/seats/images/rotate_icon.svg'
import ticketIcon from '@assets/seats/images/ticket_icon.png'

export const ZOOM = {
    MIN: 0.2,
    MAX: 2,
    RATIO: 0.1
};

class Graphic {

    get parentCenter() {
        return {
            x: this.parentElement.clientWidth / 2,
            y: this.parentElement.clientHeight / 2
        }
    }

    constructor() {
        this.isInit = false;
        this.blockImages = true;
        this.rowsLabel = {};
        this.rowsSeats = {};
    }

    log(...args) {
        console.log('[GRAPHIC]', ...args);
    }

    init(parentElement, { backgroundColor, assetsPath = '' } = {}, onLoaded = null) {
        this.log('Initialization ...');

        this.markers = [];
        this.zoom = 1;
        this.isInit = true;
        this.entityManager = new EntityManager();
        this.container = new Container();
        this.container.sortableChildren = true;
        this.parentElement = parentElement;
        this.editionsModes = [];
        this.backgroundColor = backgroundColor;
        this.onLoaded = onLoaded;
        this.resources = {};

        this.initApp();
        containerEvents.init(this.app.stage);
        this.listenEvents();

        if (!Loader.shared.resources['rotate']) {
            Loader.shared.add('rotate', rotateIcon, {crossOrigin: 'anonymous'});
        }
        if (!Loader.shared.resources['ticket']) {
            Loader.shared.add('ticket', ticketIcon, {crossOrigin: 'anonymous'});
        }

        Loader.shared.load((loader, resources) => {
            this.resources = resources;
            if (this.onLoaded) this.onLoaded();
        });

        this.setMarker('center', { x: 0, y: 0 });
    }

    listenEvents() {
        graphicEvents.on(e.SET_ROWS, args => {
            stateManager.save();
            RowService.upsertFromProps(args);
        });
        graphicEvents.on(e.SET_TEXTS, args => {
            stateManager.save();
            TextService.upsertFromProps(args);
        });
        graphicEvents.on(e.SET_PITS, args => {
            stateManager.save();
            PitService.upsertFromProps(args);
        });
        graphicEvents.on(e.SET_IMAGES, args => {
            stateManager.save();
            ImageService.upsertFromProps(args);
        });

        graphicEvents.on(REMOVE_ENTITIES, entities => {
            entities.forEach(entity => {
                if (entity) this.entityManager.remove(entity);
            });
        });

        graphicEvents.on(SET_TEXTS_DESCRIPTION, ({ ids, descriptionId, text: label }) => {
            ids.forEach(textId => {
                const text = this.entityManager.get(TYPES.TEXT, textId);
                if (!text.originalLabel) text.originalLabel = text.label;

                if (descriptionId) {
                    text.description = { id: descriptionId, label };
                    text.label = label;
                } else {
                    text.description = null;
                    text.label = text.originalLabel;
                    text.originalLabel = null;
                }

                this.entityManager.texts[text.id] = text;
            })
        });

        graphicEvents.on(UPDATE_DESCRIPTION_TEXT, ({ id, text: label }) => {
            this.entityManager.forEach(TYPES.TEXT, text => {
                if (text.description && text.description.id === id) {
                    text.label = label
                }
            })
        });

        graphicEvents.on(e.TOGGLE_SHOW_LABELS, RowService.toggleShowLabels);
        graphicEvents.on(e.CHANGE_LABEL_ROW, RowService.changeLabel);
        graphicEvents.on(e.CHANGE_LABEL_MULTIPLE_ROWS, ({ rowsId, ...rest }) => {
            RowService.changeLabelMultipleRows({
                rows: rowsId.map(id => this.entityManager.get(TYPES.ROW, id)),
                ...rest
            })
        });
        graphicEvents.on(e.CHANGE_CURVE_ROW, RowService.changeCurve);
        graphicEvents.on(e.CHANGE_SEQUENCE_ROW, RowService.changeSequenceType);
        graphicEvents.on(e.CHANGE_SEQUENCE_OFFSET_ROW, RowService.changeSequenceOffset);
        graphicEvents.on(e.CHANGE_SEATS_COUNT, RowService.changeSeatsCount);
        graphicEvents.on(e.SET_SEAT_TEXT, RowService.setSeatText);
        graphicEvents.on(e.REMOVE_SEAT_TEXT, RowService.removeSeatText);

        graphicEvents.on(e.SET_PITS_PROPS, ({ pitsIds, width, height }) => {
            pitsIds.forEach(pitId => {
                const pit = this.entityManager.get(TYPES.PIT, pitId);
                const pitProps = pit.getProps();
                if (width) pitProps.width = width;
                if (height) pitProps.height = height;
                pit.updateFromProps(pitProps);
            });
            selection.update();
        });

        graphicEvents.on(e.UPDATE_CATEGORY, category => {
            this.entityManager.forEach(TYPES.ROW, entity => {
                if (entity.category && parseInt(entity.category.id) === parseInt(category.id)) {
                    RowService.changeCategory({ rows: [entity], category });
                }
            });
            this.entityManager.forEach(TYPES.PIT, entity => {
                if (entity.category && parseInt(entity.category.id) === parseInt(category.id)) {
                    PitService.changeCategory({ pitsId: [entity.id], category });
                }
            });
        });

        graphicEvents.on(e.ASSIGN_ENTITIES_CATEGORY, ({ entities, category }) => {
            entities.rows.forEach(rowProps => {
                const row = this.entityManager.get(TYPES.ROW, rowProps.id);
                row.setCategory(category);
            });
            entities.pits.forEach(pitProps => {
                const pit = this.entityManager.get(TYPES.PIT, pitProps.id);
                pit.setCategory(category);
            });
        });

        graphicEvents.on(e.CHANGE_LABEL_TEXT, TextService.changeLabel);
        graphicEvents.on(e.CHANGE_FONT_SIZE_TEXT, TextService.changeFontSize);

        graphicEvents.on(e.CHANGE_IMAGE_RATIO, ImageService.changeRatio);

        graphicEvents.on('DECREASE_ZOOM', () => this.setZoom({
            direction: -1,
            origin: graphic.withTransformContainer(graphic.parentCenter)
        }));

        graphicEvents.on('INCREASE_ZOOM', () => this.setZoom({
            direction: 1,
            origin: graphic.withTransformContainer(graphic.parentCenter)
        }));

        graphicEvents.on('SET_SEAT_CATEGORY', ({rowId, categoryId, categories, seatReference}) => {
            const row = this.entityManager.get(TYPES.ROW, rowId);
            const label = seatReference.split('-')[1]
            row.setSeatCategory({label}, categoryId, categories);
        });
    }

    initApp() {
        this.app = new Application({
            width: this.parentElement.clientWidth,
            height: this.parentElement.clientHeight,
            backgroundColor: this.backgroundColor || 0xEEEEEE,
            antialias: true,
            forceCanvas: true
        });
        this.container.setParent(this.app.stage);
        this.app.stage.hitArea = new Rectangle(0, 0, this.parentElement.clientWidth, this.parentElement.clientHeight);
        this.parentElement.appendChild(this.app.view);
    }

    destroy() {
        this.log('Destroy ...');

        this.parentElement.removeChild(this.app.view);
        this.entityManager.removeAll();
        graphicEvents.init();
        containerEvents.destroy(this.app.stage);
    }

    center() {
        const bounds = this.container.getBounds();
        if (bounds.width > this.parentElement.clientWidth || bounds.height > this.parentElement.clientHeight) {
            const success = this.setZoom({ direction: -1 });
            if (success) this.center();
        }
        this.centerPosition(this.parentCenter);
    }

    centerPosition({ x, y }) {
        const containerBounds = this.container.getBounds();
        const containerCenter = {
            x: containerBounds.x + (containerBounds.width / 2),
            y: containerBounds.y + (containerBounds.height / 2)
        };
        this.container.position.x += Math.floor(x - containerCenter.x);
        this.container.position.y += Math.floor(y - containerCenter.y);
    }

    setEditionMode(...editionsModes) {
        this.unsetEditionMode();
        this.addEditionMode(...editionsModes);
    }

    containEditionMode(type) {
        return this.editionsModes.some(editionMode => editionMode instanceof type)
    }

    getEditionMode(type) {
        return this.editionsModes.filter(editionMode => editionMode instanceof type)[0]
    }

    addEditionMode(...editionsModes) {
        editionsModes.forEach(editionMode => {
            if (!this.editionsModes.filter(edition => editionMode instanceof edition.constructor)) return;
            containerEvents.bindEvents(editionMode);
            this.editionsModes.push(editionMode);
        });
    }

    unsetEditionMode() {
        this.editionsModes.forEach(edition => {
            if (edition instanceof SelectionEditionMode) {
                selection.destroy();
            }
            containerEvents.unbindEvents(edition);
            edition.destroy();
        });
        this.editionsModes = [];
    }

    withTransformContainer({ x, y, callback = null }) {
        if (callback) callback(this.container.position, this.zoom, this.container.pivot);
        return {
            x: ((x - this.container.x) / this.zoom) + this.container.pivot.x,
            y: ((y - this.container.y) / this.zoom) + this.container.pivot.y
        }
    }

    setMarker(name, position, color = 0x333333) {
        return;
        if (!this.markers[name]) {
            this.markers[name] = Shape.createCircle(0, 0, 10, [color, 1]);
            this.container.addChild(this.markers[name]);
        }
        this.markers[name].position = position;
    }

    translateOrigin({ x, y }) {
        const v = {
            x: x - this.lastOrigin.x,
            y: y - this.lastOrigin.y,
        };
        this.container.pivot.x = x;
        this.container.pivot.y = y;
        this.container.x += v.x * this.zoom;
        this.container.y += v.y * this.zoom;
    }

    setZoom({ round = true, origin = null, value = null, direction }) {
        if (!value && direction === -1 && this.zoom > ZOOM.MIN) {
            value = Math.round((this.zoom - ZOOM.RATIO) * 10) / 10;
        } else if (!value && direction === 1 && this.zoom < ZOOM.MAX) {
            value = Math.round((this.zoom + ZOOM.RATIO) * 10) / 10;
        }

        if (!value || value < ZOOM.MIN || value > ZOOM.MAX) return false;

        const fromOrigin = origin && (!this.lastOrigin || (this.lastOrigin.x !== origin.x || this.lastOrigin.y !== origin.y));
        this.lastOrigin = {x: this.container.pivot.x, y: this.container.pivot.y};
        if (fromOrigin) this.translateOrigin(origin);

        this.zoom = round ? Math.round(value * 10) / 10 : value;
        this.container.scale = { x: this.zoom, y: this.zoom };
        this.container.updateTransform();
        graphicEvents.trigger(ON_CHANGE_ZOOM, round ? this.zoom : Math.round(value * 10) / 10);

        return true;
    }

    translate({ x, y }) {
        this.container.x += x;
        this.container.y += y;

        if (x !== 0 || y !== 0) {
            graphicEvents.trigger(ON_CHANGE_TRANSLATE);
        }
    }

    handleDuplicatedSeats() {
        const seats = [];

        Object.keys(this.rowsLabel).forEach(rowId => {
            this.rowsSeats[rowId].forEach(seat => {
                seats.push([this.rowsLabel[rowId], seat].toString());
            });
        });
        const uniqSeats = [...new Set(seats)];
        const duplicatedCount = seats.length - uniqSeats.length;

        graphicEvents.trigger(SET_DUPLICATED_SEATS_COUNT, duplicatedCount);
    }
}

export const graphic = new Graphic();